import { useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult
} from 'react-query';
import { EXCEPTION, Tabs } from 'common/constants';
import { CashInBankAccountsType } from 'components/CashInBankAccountsDropdown/types';
import { setErrorMessages } from 'components/ErrorMessageHandler/slice';
import {
  ArchiveDataRequestPayloadType,
  ArchiveDataResponseType
} from 'components/ExportOrArchiveDialog/types';
import {
  PrintDataRequestPayloadType,
  PrintDataResponseType,
  PrinterListType
} from 'components/PrintViewer/types';
import { messages } from 'components/RemittanceFormRenderer/messages';
import {
  getCompanyId,
  getDepartmentName,
  getStoreId
} from 'components/StoreSelect/slice';
import { errorsInInterCompany } from 'pages/AuthorizePaymentPage/interCompany/messages';
import {
  CashInBankResponseType,
  ErrorResponse,
  GlValidationResponseType
} from 'pages/AuthorizePaymentPage/interCompany/types';
import { errors } from 'pages/AuthorizePaymentPage/messages';
import {
  AuthorizePaymentPayload,
  AuthorizePaymentReviewType,
  LockInvoicePayload,
  LockInvoiceResponse,
  LockStatus,
  PaymentApprovalPayload,
  UpdateInvoiceDescriptionPayload
} from 'pages/AuthorizePaymentPage/types';
import { HIGH_VOLUME_VENDORS_PAGE_SIZE } from 'pages/HighVolumeLandingPage/constants';
import { getSelectedTab } from 'pages/LandingPage/components/LandingPageHeader/slice';
import { getCompletedRecords } from 'pages/VendorPaymentResultPage/selectors';
import { AuthorizationPaymentResponse } from 'pages/VendorPaymentResultPage/types';
import { isCheckNoExists } from 'pages/VendorsTablePage/components/PrintChecks/CheckSequence/helpers';
import { errorMessageChkSeq } from 'pages/VendorsTablePage/components/PrintChecks/CheckSequence/messages';
import { setAllPrinterErrors } from 'pages/VendorsTablePage/components/PrintChecks/CheckSequence/slice';
import { PrinterAndCheckNoResponseType } from 'pages/VendorsTablePage/components/PrintChecks/CheckSequence/types';
import { errors as cibErrors } from 'pages/VendorsTablePage/components/PrintChecks/messages';
import { getSelectedDepartment } from 'pages/VendorsTablePage/components/PrintChecks/utils';
import {
  getDebouncedSearchInputValue,
  getDueDate,
  getPaymentMethods,
  getPrevSortColumn,
  getPrevSortOrder,
  getVendorsResponse
} from 'pages/VendorsTablePage/components/VendorsTable/slice';
import { errors as vendorsError } from 'pages/VendorsTablePage/messages';
import { AppDispatch } from 'store';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getResponseErrorMessage } from 'utils/getResponseErrorMessage';
import { handleJsonError } from 'utils/handleJsonError';
import { useClient } from '../components/ClientProvider';
import { setSuccessMessage } from '../components/SuccessMessage/slice';
import {
  AuthorizePaymentSuccessHelper,
  getPDF,
  IError,
  onErrorHandler
} from './helper';
import { errorMessages, labelMessages } from './messages';
import { PDFGenerationBase64Type } from './types';
import { useErrorHandler } from './useErrorHandler';
import { useFlagValue } from './useFlags';

import type {
  CheckStatusResponse,
  CheckWritingResponse,
  HeaderData,
  VoidChecksResponse
} from '../adapters/types';
import type {
  PaymentStatusUpdateDataType,
  PaymentStatusUpdateResponseDataType,
  ReconciliationReportDataType,
  VoidPaymentStatusResponseDataType,
  VoidPaymentRequestDataType
} from '../pages/ReconciliationReportPage/types';
import type {
  DeleteApprovalRequestPayload,
  OpenGlCalendarsType,
  PrintChecksCIBDropdownType,
  PrintChecksRequestInterCompanyPayload,
  PrintChecksRequestPayload,
  PrintChecksResponse,
  VoidChecksRequestPayload
} from '../pages/VendorsTablePage/components/PrintChecks/types';
import type {
  UpdateInvoiceStateRequest,
  VendorDataType,
  VendorsResponseDataType
} from '../pages/VendorsTablePage/components/VendorsTable/types';

function getSuccessMessageNeedsAuthorization(data: LockInvoiceResponse) {
  return data.successes[0].lockStatus === LockStatus.LOCKED
    ? labelMessages.invoicesLocked
    : labelMessages.invoicesUnlocked;
}

function getSuccessMessage(data: LockInvoiceResponse) {
  return data.successes[0].lockStatus === LockStatus.LOCKED
    ? labelMessages.invoiceLocked
    : labelMessages.invoiceUnlocked;
}

function getSuccessMessageLockedPrintChecks(data: LockInvoiceResponse) {
  return data.successes[0].lockStatus === LockStatus.LOCKED
    ? labelMessages.invoicesLocked
    : labelMessages.printedInvoicesUnlocked;
}

function getSuccessMessageUnLockedPrintChecks(data: LockInvoiceResponse) {
  return data.successes[0].lockStatus === LockStatus.LOCKED
    ? labelMessages.invoiceUnlocked
    : labelMessages.printedInvoiceUnlocked;
}

export const useFetchVendors = (
  companyId: string,
  storeId: string,
  dueDate?: string,
  paymentMethod?: string,
  isApproved?: boolean,
  isAuthorized?: boolean,
  selectedDepartmentName?: string
): UseQueryResult<VendorsResponseDataType> => {
  const client = useClient();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };
  const landingPageFlag = useFlagValue('dmsAccountingLandingPage');

  return useQuery(
    [
      'vendors',
      companyId,
      storeId,
      dueDate,
      paymentMethod,
      isApproved,
      isAuthorized
    ],
    () =>
      client?.data.fetchVendors(
        headers,
        companyId,
        storeId,
        dueDate,
        paymentMethod,
        isApproved,
        isAuthorized
      ),
    {
      onError: (error: IError) =>
        onErrorHandler(
          error,
          dispatch,
          intl,
          intl.formatMessage(vendorsError.vendorApiFailureMsg)
        ),
      enabled: landingPageFlag === false && !!selectedDepartmentName
    }
  );
};

export const useFetchNeedsAuthExportData = (
  enabled?: boolean
): UseQueryResult<VendorDataType[]> => {
  const client = useClient();
  const selectedPaymentMethods = useAppSelector(getPaymentMethods);
  const debouncedSearchValue = useAppSelector(getDebouncedSearchInputValue);
  const dueDate = useAppSelector(getDueDate);
  const companyId = useAppSelector(getCompanyId);
  const storeId = useAppSelector(getStoreId);
  const selectedTab = useAppSelector(getSelectedTab);
  const paymentMethod =
    selectedPaymentMethods?.length > 1 ? undefined : selectedPaymentMethods[0];
  const prevSortColumn = useAppSelector(getPrevSortColumn);
  const prevSortOrder = useAppSelector(getPrevSortOrder);
  const vendorsResponse = useAppSelector(getVendorsResponse);
  const filterData = useMemo(
    () => ({
      storeId,
      companyId,
      debouncedSearchValue,
      dueDate,
      paymentMethod,
      selectedTab
    }),
    [
      companyId,
      debouncedSearchValue,
      dueDate,
      paymentMethod,
      selectedTab,
      storeId
    ]
  );
  return useQuery<VendorDataType[], Error>(
    ['exportData', filterData, prevSortColumn, prevSortOrder],
    async () => {
      const totalNoOfVendors = vendorsResponse?.totalNoOfVendors ?? 0;
      const numPages = Math.ceil(
        totalNoOfVendors / HIGH_VOLUME_VENDORS_PAGE_SIZE
      );
      const data: VendorDataType[] = [];
      for (let page = 1; page <= numPages; page += 1) {
        // https://eslint.org/docs/latest/rules/no-await-in-loop#when-not-to-use-it
        // eslint-disable-next-line no-await-in-loop
        const pageData = await client?.data.fetchHighVolumeVendors(
          client?.headers,
          filterData.companyId,
          filterData.storeId,
          page,
          HIGH_VOLUME_VENDORS_PAGE_SIZE,
          filterData.dueDate,
          filterData.paymentMethod,
          filterData.selectedTab,
          filterData.debouncedSearchValue,
          prevSortColumn,
          prevSortOrder
        );
        const vendors = pageData?.vendors ?? [];
        data.push(...vendors);
      }
      return data;
    },
    {
      enabled,
      staleTime: 0
    }
  );
};

export const useFetchPrintAuthorizedVendors = (
  companyId: string,
  storeId: string,
  onSuccess: (data: PrintChecksResponse) => void,
  refetchOnMount?: boolean,
  retry = 0,
  retryOnMount = false,
  selectedDepartmentName?: string
): UseQueryResult<PrintChecksResponse, Error> => {
  const client = useClient();
  const onError = useErrorHandler(errorMessages.apiError);
  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };
  return useQuery(
    ['printAuthorizedVendors', companyId, storeId],
    () => client?.data.fetchPrintAuthorizedVendors(headers, companyId, storeId),
    {
      refetchOnMount,
      retry,
      retryOnMount,
      onError,
      onSuccess
    }
  );
};

export const useFetchPrintChecksCIBAccounts = (
  companyId: string,
  storeId: string,
  selectedDepartmentName: string
): UseQueryResult<PrintChecksCIBDropdownType[], Error> => {
  const client = useClient();
  const onError = useErrorHandler(cibErrors.cibAccountRequired);
  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };
  return useQuery(
    ['printChecksCIBAccounts', companyId, storeId, selectedDepartmentName],
    () => client?.data.fetchPrintChecksCIBAccounts(headers, companyId, storeId),
    {
      refetchOnMount: true,
      cacheTime: 0,
      onError,
      enabled: !!companyId && !!storeId
    }
  );
};

export const useFetchOpenGlCalendars = (): UseQueryResult<
  OpenGlCalendarsType,
  Error
> => {
  const intl = useIntl();
  const client = useClient();
  const dispatch = useAppDispatch();
  const storeId = useAppSelector(getStoreId);
  const selectedDepartmentName = useAppSelector(getDepartmentName);
  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };
  return useQuery(
    ['openGlCalendars'],
    () => client?.data.fetchGlCalendar(headers, storeId),
    {
      onError: () => {
        dispatch(
          setErrorMessages([
            intl.formatMessage(errorMessages.glCalendarApiError)
          ])
        );
      }
    }
  );
};

const getErrorMessage = (lockStatus: LockStatus) =>
  lockStatus === LockStatus.LOCKED
    ? errorMessages.invoicesNotLocked
    : errorMessages.invoicesNotUnlocked;

const handleSuccessMessage = (
  dispatch: AppDispatch,
  data: LockInvoiceResponse,
  selectedTab: string,
  intl: IntlShape
) => {
  if (data.successes.length === 1 && selectedTab === Tabs.PrintChecks) {
    const oneSuccessMessage = getSuccessMessageUnLockedPrintChecks(data);
    dispatch(setSuccessMessage(intl.formatMessage(oneSuccessMessage)));
  } else if (data.successes.length > 1 && selectedTab === Tabs.PrintChecks) {
    const successMessage = getSuccessMessageLockedPrintChecks(data);
    dispatch(setSuccessMessage(intl.formatMessage(successMessage)));
  } else if (
    (data.successes.length === 1 && selectedTab === Tabs.NeedsAuthorization) ||
    selectedTab === Tabs.ToBePaid
  ) {
    const successMessage = getSuccessMessage(data);
    dispatch(setSuccessMessage(intl.formatMessage(successMessage)));
  } else if (
    data.successes.length > 1 &&
    selectedTab === Tabs.NeedsAuthorization
  ) {
    const successMessage = getSuccessMessageNeedsAuthorization(data);
    dispatch(setSuccessMessage(intl.formatMessage(successMessage)));
  }
};

export const useUpdateInvoiceLockStatus = (): UseMutationResult<
  LockInvoiceResponse,
  unknown,
  LockInvoicePayload,
  unknown
> => {
  const dispatch = useAppDispatch();
  const client = useClient();
  const intl = useIntl();
  const storeId = useAppSelector(getStoreId);
  const selectedTab = useAppSelector(getSelectedTab);
  const selectedDepartmentName = useAppSelector(getDepartmentName);

  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };
  const onError = useErrorHandler(errorMessages.updateLockStatus);
  return useMutation(
    (reqObj: LockInvoicePayload): Promise<LockInvoiceResponse> =>
      client?.data.updateInvoiceLockStatus(
        reqObj.invoices,
        headers,
        storeId,
        reqObj.lockStatus
      ) as Promise<LockInvoiceResponse>,
    {
      onSuccess: (data, req) => {
        if (data.failures.length) {
          const errorMessage = getErrorMessage(req.lockStatus);
          dispatch(
            setErrorMessages([
              intl.formatMessage(errorMessage, {
                number: data.failures.length
              })
            ])
          );
        } else if (data.successes.length) {
          handleSuccessMessage(dispatch, data, selectedTab, intl);
        }
      },
      onError
    }
  );
};

export const usePrintChecks = (): UseMutationResult<
  CheckWritingResponse,
  Error,
  PrintChecksRequestPayload,
  unknown
> => {
  const client = useClient();
  const companyId = useAppSelector(getCompanyId);
  const storeId = useAppSelector(getStoreId);
  const selectedDepartmentName = useAppSelector(getDepartmentName);

  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };
  return useMutation(
    (reqObj: PrintChecksRequestPayload): Promise<CheckWritingResponse> =>
      client?.data.printChecks(
        headers,
        companyId,
        storeId,
        reqObj
      ) as Promise<CheckWritingResponse>
  );
};

export const useInterCompanyPrintChecks = (
  onUpdate: (status: CheckStatusResponse) => void
): UseMutationResult<
  CheckWritingResponse,
  IError,
  PrintChecksRequestInterCompanyPayload,
  unknown
> => {
  const client = useClient();
  const companyId = useAppSelector(getCompanyId);
  const storeId = useAppSelector(getStoreId);
  const selectedDepartmentName = useAppSelector(getDepartmentName);
  const dispatch = useAppDispatch();
  const intl = useIntl();

  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };
  return useMutation(
    (
      reqObj: PrintChecksRequestInterCompanyPayload
    ): Promise<CheckWritingResponse> =>
      client?.data.printInterCompanyChecks(
        headers,
        companyId,
        storeId,
        reqObj,
        onUpdate
      ) as Promise<CheckWritingResponse>,
    {
      onError: (error: IError) => {
        const parsedErrorMessg = handleJsonError(error.message);
        const isAlreadyUsedCheckNo = isCheckNoExists(
          parsedErrorMessg as ErrorResponse
        );
        const messageExists = getResponseErrorMessage(
          parsedErrorMessg as ErrorResponse
        );

        if (isAlreadyUsedCheckNo) {
          dispatch(
            setErrorMessages([
              intl.formatMessage(errorMessageChkSeq.alreadyUsedCheckNo)
            ])
          );
        } else if (messageExists) {
          dispatch(
            setErrorMessages([
              intl.formatMessage(errorsInInterCompany.errorFromAPI, {
                APIError: messageExists.split(EXCEPTION).pop()
              })
            ])
          );
        } else {
          dispatch(
            setErrorMessages([
              intl.formatMessage(errorMessages.printChecksApiError)
            ])
          );
        }
      }
    }
  );
};

export const useVoidChecks = (): UseMutationResult<
  VoidChecksResponse,
  Error,
  VoidChecksRequestPayload,
  unknown
> => {
  const client = useClient();
  const selectedDepartmentName = useAppSelector(getDepartmentName);
  const companyId = useAppSelector(getCompanyId);
  const storeId = useAppSelector(getStoreId);
  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };
  return useMutation(
    (reqPayload: VoidChecksRequestPayload): Promise<VoidChecksResponse> =>
      client?.data.voidChecks(
        headers,
        companyId,
        storeId,
        reqPayload
      ) as Promise<VoidChecksResponse>
  );
};
export const useFetchReconciliationReport = (
  statuses: string
): UseQueryResult<ReconciliationReportDataType[], Error> => {
  const successPaymentData = useAppSelector(getCompletedRecords);
  const client = useClient();
  const storeId = useAppSelector(getStoreId);
  const companyId = useAppSelector(getCompanyId);
  const selectedDepartmentName = useAppSelector(getDepartmentName);
  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };
  return useQuery(
    ['reconciliationReport', companyId, storeId, statuses, successPaymentData],
    () =>
      client?.data.fetchReconciliationReport(
        statuses,
        companyId,
        storeId,
        headers
      )
  );
};

export const useUpdateReconciliationReportPaymentStatus = (): UseMutationResult<
  PaymentStatusUpdateResponseDataType,
  unknown,
  PaymentStatusUpdateDataType,
  unknown
> => {
  const client = useClient();
  const storeId = useAppSelector(getStoreId);
  const selectedDepartmentName = useAppSelector(getDepartmentName);

  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };
  const response = useMutation(
    (
      reqObj: PaymentStatusUpdateDataType
    ): Promise<PaymentStatusUpdateResponseDataType> =>
      client?.data.updateReconciliationPaymentStatus(
        reqObj,
        headers,
        storeId
      ) as Promise<PaymentStatusUpdateResponseDataType>
  );
  return response;
};

export const useVoidReconciliationPayment = (): UseMutationResult<
  VoidPaymentStatusResponseDataType,
  Error,
  VoidPaymentRequestDataType,
  unknown
> => {
  const client = useClient();
  const companyId = useAppSelector(getCompanyId);
  const storeId = useAppSelector(getStoreId);
  const selectedDepartmentName = useAppSelector(getDepartmentName);

  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };
  return useMutation(
    (
      reqPayload: VoidPaymentRequestDataType
    ): Promise<VoidPaymentStatusResponseDataType> =>
      client?.data.voidReconciliationPayment(
        headers,
        companyId,
        storeId,
        reqPayload
      ) as Promise<VoidPaymentStatusResponseDataType>
  );
};

export const useDeleteApproval = (
  onSuccess?: () => void
): UseMutationResult<
  unknown,
  unknown,
  DeleteApprovalRequestPayload,
  unknown
> => {
  const client = useClient();
  const storeId = useAppSelector(getStoreId);
  const companyId = useAppSelector(getCompanyId);
  const selectedDepartmentName = useAppSelector(getDepartmentName);

  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };
  const onError = useErrorHandler(errors.deleteApproval);
  const response = useMutation(
    (reqObj: DeleteApprovalRequestPayload): Promise<void> =>
      client?.data.deleteApproval(
        headers,
        companyId,
        storeId,
        reqObj
      ) as Promise<void>,
    { onSuccess, onError }
  );
  return response;
};

export const useSubmitAuthorizedPayment = (
  retrySubmit = false
): UseMutationResult<
  AuthorizationPaymentResponse,
  IError,
  AuthorizePaymentReviewType,
  unknown
> => {
  const client = useClient();
  const intl = useIntl();
  const storeId = useAppSelector(getStoreId);
  const dispatch = useAppDispatch();
  const selectedDepartmentName = useAppSelector(getDepartmentName);
  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };
  const { mutate: deleteApprovals } = useDeleteApproval();
  const { mutate: updateLockStatus } = useUpdateInvoiceLockStatus();
  const response = useMutation(
    (
      reqObj: AuthorizePaymentReviewType
    ): Promise<AuthorizationPaymentResponse> =>
      client?.data.postAuthorizedPayment(
        reqObj,
        headers,
        storeId
      ) as Promise<AuthorizationPaymentResponse>,
    {
      onSuccess: data => {
        AuthorizePaymentSuccessHelper(data, retrySubmit, dispatch);
        const invoices = data?.listOfVendors?.flatMap(
          ({ failReason, transactionId, vendorNo, listOfInvoices }) =>
            transactionId && !failReason
              ? listOfInvoices.map(({ invoiceId }) => ({ invoiceId, vendorNo }))
              : []
        );
        if (invoices?.length) {
          updateLockStatus(
            { lockStatus: LockStatus.UNLOCKED, invoices },
            {
              onSuccess: ({ successes }) => deleteApprovals(successes)
            }
          );
        }
      },
      onError: (error: IError) => {
        dispatch(
          setErrorMessages([
            error?.message ||
              intl.formatMessage(errorMessages.virtualCardApiError)
          ])
        );
      }
    }
  );
  return response;
};

export const useGlValidation = (): UseMutationResult<
  GlValidationResponseType,
  unknown,
  AuthorizePaymentReviewType,
  unknown
> => {
  const client = useClient();
  const storeId = useAppSelector(getStoreId);
  const selectedDepartmentName = useAppSelector(getDepartmentName);
  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };
  const response = useMutation(
    (reqObj: AuthorizePaymentReviewType): Promise<GlValidationResponseType> =>
      client?.data.glPostingValidation(
        reqObj,
        headers,
        storeId
      ) as Promise<GlValidationResponseType>
  );
  return response;
};

export const usePostPaymentApprovals = (): UseMutationResult<
  void,
  Error,
  PaymentApprovalPayload
> => {
  const client = useClient();
  const companyId = useAppSelector(getCompanyId);
  const storeId = useAppSelector(getStoreId);
  const selectedDepartmentName = useAppSelector(getDepartmentName);

  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };
  const response = useMutation<void, Error, PaymentApprovalPayload>(
    (reqObj: PaymentApprovalPayload): Promise<void> =>
      client?.data.postPaymentApproval(
        reqObj,
        headers,
        storeId,
        companyId
      ) as Promise<void>
  );
  return response;
};

export const useUpdateInvoiceState = (): UseMutationResult<
  unknown,
  unknown,
  UpdateInvoiceStateRequest,
  unknown
> => {
  const client = useClient();
  const selectedDepartmentName = useAppSelector(getDepartmentName);
  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };
  const onError = useErrorHandler(errors.authorizePayment);
  const storeId = useAppSelector(getStoreId);
  const response = useMutation(
    ({ payload, state }: UpdateInvoiceStateRequest): Promise<void> =>
      client?.data.updateInvoiceState(
        headers,
        storeId,
        state,
        payload
      ) as Promise<void>,
    { onError }
  );
  return response;
};

export const useSubmitAuthorizedApCheck = (
  isAuthorized: boolean
): UseMutationResult<string, Error, AuthorizePaymentPayload> => {
  const client = useClient();
  const companyId = useAppSelector(getCompanyId);
  const storeId = useAppSelector(getStoreId);
  const selectedDepartmentName = useAppSelector(getDepartmentName);

  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };
  const onError = useErrorHandler(errors.authorizePayment);
  const response = useMutation<string, Error, AuthorizePaymentPayload>(
    (reqObj: AuthorizePaymentPayload): Promise<string> =>
      client?.data.updatePaymentIsAuthorized(
        reqObj,
        headers,
        storeId,
        companyId,
        isAuthorized
      ) as Promise<string>,
    { onError }
  );
  return response;
};

export const useFetchCashInBankAccounts = (): UseQueryResult<
  CashInBankAccountsType[],
  Error
> => {
  const client = useClient();
  const storeId = useAppSelector(getStoreId);
  const companyId = useAppSelector(getCompanyId);
  const selectedDepartmentName = useAppSelector(getDepartmentName);
  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };
  return useQuery(['cashInBankAccounts', storeId], () =>
    client?.data.fetchCashInBankAccounts(headers, storeId, companyId)
  );
};

export const useFetchCashInBankInterCompany = (
  printerInfo: boolean
): UseQueryResult<CashInBankResponseType, ErrorResponse> => {
  const client = useClient();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const storeId = useAppSelector(getStoreId);
  const selectedDepartmentName = useAppSelector(getDepartmentName);
  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };

  return useQuery(
    ['cashInBankInterCompany', storeId, printerInfo],
    () =>
      client?.data.fetchCashInBankInterCompany(headers, storeId, printerInfo),
    {
      onError: (error: ErrorResponse) => {
        const messageExists = getResponseErrorMessage(error);
        if (messageExists) {
          dispatch(
            setErrorMessages([
              intl.formatMessage(errorsInInterCompany.errorFromAPI, {
                APIError: messageExists
              })
            ])
          );
        } else {
          dispatch(
            setErrorMessages([
              intl.formatMessage(errorsInInterCompany.cashInBank)
            ])
          );
        }
      },
      cacheTime: 0
    }
  );
};

export const useFetchPrintersWithCheckNo = (
  cashInBank: string,
  cashCompanyStoreid: string
): UseQueryResult<PrinterAndCheckNoResponseType[], ErrorResponse> => {
  const intl = useIntl();
  const client = useClient();
  const deptIds = client?.customClientValues?.departments;
  const dispatch = useAppDispatch();
  const companyId = useAppSelector(getCompanyId);
  const checkSequencingFlag = useFlagValue('dmsAccountingCheckSequencing');
  const selectedDepartment = getSelectedDepartment(deptIds, cashCompanyStoreid);
  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartment?.departmentName
  };

  return useQuery(
    ['printersInfoCheckSequence', cashInBank],
    () =>
      client?.data.fetchPrintersWithCheckNo(
        headers,
        companyId,
        cashCompanyStoreid,
        cashInBank
      ),
    {
      onError: () => {
        dispatch(
          setErrorMessages([intl.formatMessage(errorMessageChkSeq.printerNo)])
        );
        dispatch(
          setAllPrinterErrors({
            printerError: true,
            checkTrayError: true,
            overflowTrayError: true
          })
        );
      },
      cacheTime: 0,
      enabled:
        cashInBank !== undefined &&
        cashInBank !== '' &&
        checkSequencingFlag === true
    }
  );
};

export const useFetchBase64 = (
  documentId: string,
  isRCC: boolean
): UseQueryResult<PDFGenerationBase64Type, Error> => {
  const client = useClient();
  const storeId = useAppSelector(getStoreId);
  const selectedDepartmentName = useAppSelector(getDepartmentName);

  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };
  const onError = useErrorHandler(
    isRCC ? messages.docDownloadFailed : messages.remittanceReceiptFailed
  );
  return useQuery(
    ['pdfGeneration', documentId, storeId],
    () => client?.data.fetchPDFGenerationBase64(headers, documentId, storeId),
    {
      enabled: false,
      onSuccess: (data: PDFGenerationBase64Type) => {
        getPDF(data.content);
      },
      onError
    }
  );
};

export const useFetchPrinterList = (
  enabled: boolean
): UseQueryResult<PrinterListType, Error> => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const client = useClient();
  const storeId = useAppSelector(getStoreId);
  return useQuery(
    ['printerList', storeId],
    () => client?.data.fetchPrinterList(client?.headers, storeId),
    {
      onError: () => {
        dispatch(
          setErrorMessages([
            intl.formatMessage(labelMessages.printerListsFailed)
          ])
        );
      },
      cacheTime: 0,
      enabled
    }
  );
};

export const useSubmitPrintData = (): UseMutationResult<
  PrintDataResponseType,
  unknown,
  PrintDataRequestPayloadType,
  unknown
> => {
  const client = useClient();
  const storeId = useAppSelector(getStoreId);
  const selectedDepartmentName = useAppSelector(getDepartmentName);

  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };
  const response = useMutation(
    (reqObj: PrintDataRequestPayloadType): Promise<PrintDataResponseType> =>
      client?.data.postPrintData(
        headers,
        storeId,
        reqObj
      ) as Promise<PrintDataResponseType>
  );
  return response;
};

export const useUpdateInvoiceDescriptions = (): UseMutationResult<
  void,
  unknown,
  UpdateInvoiceDescriptionPayload,
  unknown
> => {
  const client = useClient();
  const storeId = useAppSelector(getStoreId);
  return useMutation(
    (reqObj: UpdateInvoiceDescriptionPayload): Promise<void> =>
      client?.data.updateInvoiceDescriptions(
        reqObj,
        client?.headers,
        storeId
      ) as Promise<void>
  );
};

export const useSubmitArchiveData = (
  fileExtension: string
): UseMutationResult<
  ArchiveDataResponseType,
  unknown,
  {
    formData: ArchiveDataRequestPayloadType;
    archivePaymentMethod: string;
  },
  unknown
> => {
  const client = useClient();
  const storeId = useAppSelector(getStoreId);
  const companyId = useAppSelector(getCompanyId);
  return useMutation(
    ({ formData, archivePaymentMethod }) =>
      client?.data.postArchiveData(
        client?.headers,
        storeId,
        companyId,
        formData,
        fileExtension,
        archivePaymentMethod
      ) as Promise<ArchiveDataResponseType>
  );
};
